<template>
  <modal-fullscreen width="900px" :visible="visible" :destroy-on-close="true" @ok="closeModal" @cancel="closeModal">
    <modal-navigation
      v-if="paymentsList.length && currentIndex !== -1"
      :current="currentIndex"
      :disabled="loadingPayment"
      :total="paymentsList.length"
      @navigate="navigate"
    />

    <div slot="title">
      <div class="flex align-items-center gap-2">
        <h2 class="m-0">
          {{ title }}
        </h2>
        <a-tooltip>
          <template slot="title"> Copy Link </template>
          <span @click="copyLink" class="action-button"><a-icon width="20" height="20" icon="carbon:link" /></span>
        </a-tooltip>
      </div>
    </div>
    <div v-if="loadingPayment || !paymentInfo.payment" class="loading-spin">
      <a-spin />
    </div>

    <template v-else>
      <info-documents :payment-info="paymentInfo" />
      <a-modal
        title="Decline Shift Payments"
        :visible="visibleDeclineModal"
        width="350px"
        @cancel="visibleDeclineModal = false"
        :destroy-on-close="true"
      >
        <a-textarea v-model="cancelReason" placeholder="Enter Decline Reason" />
        <template slot="footer">
          <div class="flex justify-content-between">
            <a-button type="danger" @click="closeDeclineModal"> Cancel </a-button>
            <a-button type="primary" :disabled="!cancelReason.trim()" :loading="loadingDecline" @click="declineShifts">
              Decline
            </a-button>
          </div>
        </template>
      </a-modal>

      <div class="flex flex-column gap-2">
        <payment-balance v-if="availableSystem && !isStripeV2Payment" @updateBalance="paymentBalance = $event" />

        <a-alert
          v-if="paymentFundingAlert"
          :message="paymentFundingAlert.message"
          :type="paymentFundingAlert.type"
          :role="paymentFundingAlert.type === 'error' ? 'alert' : 'status'"
          :aria-live="paymentFundingAlert.type === 'error' ? 'assertive' : 'polite'"
          show-icon
        >
          <template slot="description">
            <span>{{ paymentFundingAlert.description }}</span>
            <a-button
              v-if="paymentFundingQuoteState === 'error'"
              class="ml-1"
              type="link"
              size="small"
              @click="refreshPaymentFundingQuote"
            >
              Try Again
            </a-button>
          </template>
        </a-alert>

        <payment-adjust-total-history
          :payment-id="paymentInfo.payment.id"
          :payment-current-total="paymentInfo.payment.total"
          :payment-original-total="paymentInfo.payment.fixed_total"
          :read-only="isCurrentProjectSender"
          @updated="refreshPaymentInfo"
        />

        <info-main
          :payment-info="paymentInfo"
          :reference-number.sync="referenceNumber"
          :available-shift-validation="availableShiftValidation"
        />

        <stripe-intercompany-status
          v-if="intercompanyInvoice || intercompanyInvoiceStatusUnavailable"
          :invoice="intercompanyInvoice"
          :loading="loadingIntercompanyInvoice"
          :status-unavailable="intercompanyInvoiceStatusUnavailable"
          @refresh="refreshIntercompanyInvoice"
          @manage-bank-account="openBankAccountSettings"
        />

        <info-shifts
          :payment-info="paymentInfo"
          :selected-row-keys.sync="selectedShifts"
          :available-validation="availableShiftValidation"
          :read-only="isCurrentProjectSender"
          @comment-updated="updateShiftPaymentComment"
        />

        <payment-attachments :files="paymentInfo.files" :is-view="isCurrentProjectSender" @save="saveAttachmentsIds" />

        <div
          v-if="
            availablePaymentValidation &&
            !isStripeV2Payment &&
            paymentBalance !== null &&
            paymentBalance < paymentInfo.payment.total &&
            availableSystem
          "
        >
          <a-alert message="Warning" type="warning" show-icon>
            <span slot="description">
              Looks like there aren't enough funds on your Stripe balance to process the payment. Right now, it's
              sitting at just
              {{ paymentBalance / 100 }}$. Please hop over to Stripe and top it up by
              {{ (paymentInfo.payment.total - paymentBalance) / 100 }}$!
            </span>
          </a-alert>
        </div>
      </div>

      <template slot="footer">
        <div
          class="flex justify-content-between"
          v-if="
            !isCurrentProjectSender &&
            hasAnyUncontrolledPermission(['validate_payments', 'process_payments'], {
              id: paymentInfo.associate.id
            })
          "
        >
          <a-button icon="delete" type="danger" ghost :disabled="!enableDelete" @click="deleteSelectedPayment"
            >Delete</a-button
          >
          <div class="flex justify-content-end" v-if="availableShiftValidation">
            <a-button
              :disabled="!selectedShifts.length || loadingApprove"
              type="danger"
              icon="close"
              :loading="loadingDecline"
              @click="visibleDeclineModal = true"
            >
              Decline
              {{ selectedShifts.length ?? ' ' }}
              Shifts
            </a-button>
            <a-button
              :disabled="!selectedShifts.length || loadingDecline"
              type="primary"
              icon="check"
              :loading="loadingApprove"
              @click="approveShifts"
            >
              Approve
              {{ selectedShifts.length ?? ' ' }}
              Shifts
            </a-button>
          </div>

          <div class="flex justify-content-end" v-else-if="availablePaymentValidation">
            <a-button :loading="loadingDecline" :disabled="loadingApprove" icon="check" @click="rejectPayment">
              Re-Review
            </a-button>
            <a-button
              type="primary"
              :loading="loadingApprove"
              :disabled="paymentApprovalDisabled"
              icon="check"
              @click="confirmPayment"
            >
              Approve Payment
            </a-button>
          </div>
        </div>
      </template>
    </template>
  </modal-fullscreen>
</template>
<script>
import { Button, notification, Spin, Input, Modal, Alert, message } from 'ant-design-vue'
import Util from '@/util'
import { createNamespacedHelpers } from 'vuex'
import PaymentInfoShifts from './payment-info-shifts.vue'
import PaymentSystemBalance from '@/modules/payment-system/components/payment-system-balance.vue'
import ShiftFormAttachments from '@/components/form-attachments.vue'
import { REQUEST_TYPE, isStripeV2PaymentSystem } from '@/store/modules/payment/constants'
import permissionMixin from '@/mixins/permissionMixin'
import PaymentInfoMain from './payment-info-main.vue'
import PaymentInfoDocuments from './payment-info-documents.vue'
import PaymentAdjustTotalHistory from '../payment-adjust-total-history.vue'
import StripeIntercompanyStatus from '../stripe/stripe-intercompany-status.vue'
import ModalNavigation from '@/components/entities/modal/modal-navigation.vue'
import { buildProjectDeepLinkQuery } from '@/utils/deep-link'
import { getRequestPaymentResumeStep } from '../../services/request-payment-utils'

const { mapActions: paymentActions, mapGetters: paymentGetters } = createNamespacedHelpers('payment')
const { mapActions: stripeActions } = createNamespacedHelpers('stripe')
const { mapActions: modalActions } = createNamespacedHelpers('modals')

export default {
  data() {
    return {
      cancelReason: '',

      loading: false,
      loadingPayment: false,
      loadingDecline: false,
      loadingApprove: false,
      loadingIntercompanyInvoice: false,

      visibleDeclineModal: false,
      visible: false,

      selectedShifts: [],
      attachmentsIds: [],

      referenceNumber: undefined,

      paymentBalance: null,
      currentIndex: -1,

      paymentInfo: {},
      paymentFundingQuote: null,
      paymentFundingQuoteState: 'idle',
      paymentFundingQuoteFetchSequence: 0,
      intercompanyInvoice: null,
      intercompanyInvoiceStatusUnavailable: false,
      paymentFetchSequence: 0,
      intercompanyFetchSequence: 0
    }
  },
  props: {
    paymentId: {
      type: Number,
      default: undefined
    },
    paymentsList: {
      type: Array,
      default: () => []
    }
  },
  mixins: [permissionMixin],
  components: {
    'a-button': Button,
    'a-spin': Spin,
    'a-textarea': Input.TextArea,
    'a-modal': Modal,
    'a-alert': Alert,
    'modal-navigation': ModalNavigation,
    'info-shifts': PaymentInfoShifts,
    'payment-adjust-total-history': PaymentAdjustTotalHistory,
    'stripe-intercompany-status': StripeIntercompanyStatus,
    'payment-balance': PaymentSystemBalance,
    'payment-attachments': ShiftFormAttachments,
    'info-main': PaymentInfoMain,
    'info-documents': PaymentInfoDocuments
  },
  watch: {
    paymentId(curr, prev) {
      if (prev && !curr) {
        this.visible = false
      } else {
        this.currentIndex = this.paymentsList.findIndex(item => item === curr)
        this.fetchPaymentInfo()
      }
    },
    paymentInfo: {
      handler(newVal) {
        this.referenceNumber = newVal?.payment?.reference_number
      },
      deep: true,
      immediate: true
    },
    currentProjectId(curr, prev) {
      if (prev && curr !== prev && this.paymentId) {
        this.fetchPaymentInfo()
      }
    }
  },
  computed: {
    ...paymentGetters(['getPaymentStatus', 'getStripePaymentSystem', 'getPaymentSystem']),

    enableDelete() {
      const payment = this.paymentInfo.payment
      const isManual = payment.method === 'Manual' && payment.manual_payment_method != null
      const isAllowedStatus = ['COMPLETED', 'PENDING'].includes(payment.status)
      const hasPermission = this.hasAnyPermission(['validate_payments', 'process_payments'])

      const canProcessPayment = (isManual || isAllowedStatus) && hasPermission

      return !this.isCurrentProjectSender && canProcessPayment
    },

    availableSystem() {
      return this.stripePaymentSystem?.id
    },

    stripePaymentSystem() {
      return this.getStripePaymentSystem(this.paymentInfo.payment?.method_id)
    },

    isStripeV2Payment() {
      return isStripeV2PaymentSystem(this.stripePaymentSystem)
    },

    spendableStripeBalance() {
      if (!this.paymentFundingQuote) return 0

      return Math.max(
        this.paymentFundingQuote.available_balance_amount - this.paymentFundingQuote.reserved_balance_amount,
        0
      )
    },

    isStripeBalanceOnlyInsufficient() {
      const quote = this.paymentFundingQuote

      return quote?.money_source === 'stripe_balance_only' && this.spendableStripeBalance < quote.payment_total_amount
    },

    paymentFundingAlert() {
      if (!this.availablePaymentValidation || !this.isStripeV2Payment) return null

      if (this.paymentFundingQuoteState === 'loading') {
        return {
          type: 'info',
          message: 'Loading payment source details',
          description: 'Approval is available after the current funding details are loaded.'
        }
      }

      if (this.paymentFundingQuoteState === 'error') {
        return {
          type: 'error',
          message: 'Payment source details are unavailable',
          description: 'Reload the funding details before approving this Payment Request.'
        }
      }

      if (this.paymentFundingQuoteState !== 'ready' || !this.paymentFundingQuote) return null

      const quote = this.paymentFundingQuote
      const paymentAmount = this.formatMoney(quote.payment_total_amount, quote.currency)
      const feeAmount = this.formatMoney(quote.total_fee_amount, quote.currency)
      const recipientAmount = this.formatMoney(quote.recipient_net_amount, quote.currency)
      const estimate = `Estimated Stripe fee: ${feeAmount}. Recipient amount: ${recipientAmount}.`

      if (quote.money_source === 'stripe_balance_only') {
        if (this.isStripeBalanceOnlyInsufficient) {
          const available = this.formatMoney(this.spendableStripeBalance, quote.currency)
          const shortfall = this.formatMoney(quote.payment_total_amount - this.spendableStripeBalance, quote.currency)

          return {
            type: 'warning',
            message: 'Stripe balance is too low',
            description: `Available after reservations: ${available}. This Payment Request needs ${shortfall} more before it can be approved. Bank account fallback is disabled. ${estimate}`
          }
        }

        return {
          type: 'info',
          message: 'Stripe balance only',
          description: `The full payment amount of ${paymentAmount} will use Stripe balance. ${estimate}`
        }
      }

      if (quote.money_source === 'stripe_balance_first') {
        const stripeAmount = this.formatMoney(quote.connected_balance_gross_amount, quote.currency)
        const bankAmount = this.formatMoney(quote.ach_debit_gross_amount, quote.currency)

        return {
          type: 'info',
          message: 'Stripe balance first',
          description: `This Payment Request will use ${stripeAmount} from Stripe balance and ${bankAmount} from the company bank account. ${estimate}`
        }
      }

      if (quote.money_source === 'bank_account_only') {
        return {
          type: 'info',
          message: 'Bank account only',
          description: `The full payment amount of ${paymentAmount} will be debited from the company bank account. ${estimate}`
        }
      }

      return {
        type: 'error',
        message: 'Payment source is unavailable',
        description: 'Choose a supported payment source before approving this Payment Request.'
      }
    },

    paymentApprovalDisabled() {
      if (this.loadingDecline) return true

      if (this.isStripeV2Payment) {
        return this.paymentFundingQuoteState !== 'ready' || this.isStripeBalanceOnlyInsufficient
      }

      return Boolean(
        this.availableSystem && (this.paymentBalance === null || this.paymentBalance < this.paymentInfo.payment.total)
      )
    },

    currentProjectId() {
      return this.$store.state.applicationState.currentProject?.id
    },

    isCurrentProjectSender() {
      const payment = this.paymentInfo.payment
      const senderId = payment?.sender?.id

      return (
        payment?.sender_type === 'project' && senderId != null && Number(senderId) === Number(this.currentProjectId)
      )
    },

    title() {
      return this.paymentInfo.payment ? REQUEST_TYPE[this.paymentInfo.payment.type] : 'Payment'
    },

    combinedPaymentSystem() {
      let method = '-'

      if (this.paymentInfo.payment && this.paymentInfo.payment.method) {
        method = Util.capitalize(this.paymentInfo.payment.method)
      }
      if (this.paymentInfo.payment.method && this.paymentInfo.payment.manual_payment_method) {
        method += ' ' + '(' + Util.splitAndCapitalize(this.paymentInfo.payment.manual_payment_method) + ')'
      }

      return method
    },

    availableShiftValidation() {
      return (
        !this.isCurrentProjectSender &&
        this.paymentInfo.payment.next_action === 'wait_for_shifts_approval' &&
        this.$store.state.applicationState.associate.permissions.some(item => item.name === 'validate_payments')
      )
    },

    availablePaymentValidation() {
      return (
        !this.isCurrentProjectSender &&
        this.paymentInfo.payment.next_action === 'wait_for_payment_approval' &&
        this.$store.state.applicationState.associate.permissions.some(item => item.name === 'process_payments')
      )
    }
  },
  methods: {
    ...paymentActions([
      'getPayment',
      'approvePaymentRequest',
      'approveShiftPayments',
      'rejectShiftPayments',
      'declineShiftPayments',
      'updatePaymentAttachments',
      'deletePayment'
    ]),
    ...stripeActions({
      getStripeIntercompanyInvoice: 'getIntercompanyInvoice',
      getPaymentFundingQuote: 'getPaymentFundingQuote'
    }),
    ...modalActions(['openModal']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    closeDeclineModal() {
      this.visibleDeclineModal = false
      this.cancelReason = ''
    },

    closeModal() {
      this.paymentFetchSequence += 1
      this.intercompanyFetchSequence += 1
      this.resetPaymentFundingQuote()
      this.visible = false
      this.selectedShifts = []
      this.loadingIntercompanyInvoice = false
      this.intercompanyInvoice = null
      this.intercompanyInvoiceStatusUnavailable = false
      this.$emit('update:paymentId', undefined)
    },

    openRequestPayment(payment) {
      this.openModal({
        type: 'requestPayment',
        data: {
          visible: true,
          initialPaymentId: payment.id,
          initialStep: getRequestPaymentResumeStep(payment.status),
          onCompleted: () => this.$emit('change', {}, true)
        }
      })
      this.closeModal()
    },

    async navigate(index) {
      this.currentIndex = index

      this.$emit('update:paymentId', this.paymentsList[index])
    },

    copyLink() {
      const mergedQuery = buildProjectDeepLinkQuery({
        currentQuery: this.$route.query,
        deepLinkQuery: {
          openPayment: this.paymentInfo.payment.id
        },
        projectId: this.$store.state.applicationState.currentProject?.id
      })

      const resolved = this.$router.resolve({
        name: this.$route.name,
        query: mergedQuery
      })

      const fullUrl = window.location.origin + resolved.href

      navigator.clipboard
        .writeText(fullUrl)
        .then(() => {
          message.success('Link  successfully copied!', 5)
        })
        .catch(() => {
          message.error('Unable copy link!', 5)
        })
    },

    confirmPayment() {
      if (this.paymentApprovalDisabled) return

      const system = this.getPaymentSystem(this.paymentInfo.payment.method_id)

      if (system && system.template_id === 4) {
        const content =
          this.paymentInfo.payment.type === 'company_invoice'
            ? 'Please ensure you are fully prepared to send this payment by bank transfer to the company. Once the payment is confirmed, the company will receive the transfer.'
            : 'Please ensure you are fully prepared to issue the payment check to the individual. Once the payment is confirmed, they will approach you to receive it.'

        Modal.confirm({
          title: 'Confirm Ready to Pay',
          content,
          onOk: () => {
            this.approvePayment()
          },
          onCancel() {}
        })
      } else {
        this.approvePayment()
      }
    },

    async saveAttachmentsIds(ids) {
      if (this.isCurrentProjectSender) return

      this.attachmentsIds = [...ids.map(item => item.id)]

      try {
        const payload = {
          files: this.attachmentsIds,
          payment_id: this.paymentId
        }

        const files = await this.updatePaymentAttachments(payload)

        this.paymentInfo = {
          ...this.paymentInfo,
          files
        }

        this.showNotification('success', 'Success', 'Attachments were successfully changed')
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      }
    },

    async approvePayment() {
      if (this.isCurrentProjectSender || this.paymentApprovalDisabled) return

      this.loadingApprove = true

      try {
        const payload = {
          request_id: this.paymentId
        }

        await this.approvePaymentRequest(payload)
        this.$emit('change', {}, true)
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.closeModal()
        this.loadingApprove = false
      }
    },

    formatMoney(amount, currency = 'usd') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: String(currency || 'usd').toUpperCase()
      }).format(Number(amount) / 100)
    },

    resetPaymentFundingQuote() {
      this.paymentFundingQuoteFetchSequence += 1
      this.paymentFundingQuote = null
      this.paymentFundingQuoteState = 'idle'
    },

    async refreshPaymentFundingQuote() {
      const payment = this.paymentInfo.payment

      if (!payment?.id || !this.availablePaymentValidation || !this.isStripeV2Payment) {
        this.resetPaymentFundingQuote()
        return
      }

      const paymentId = payment.id
      const fetchSequence = ++this.paymentFundingQuoteFetchSequence
      this.paymentFundingQuote = null
      this.paymentFundingQuoteState = 'loading'

      try {
        const quote = await this.getPaymentFundingQuote({ payment_request_id: paymentId })
        if (fetchSequence !== this.paymentFundingQuoteFetchSequence || paymentId !== this.paymentId) return

        this.paymentFundingQuote = quote
        this.paymentFundingQuoteState = 'ready'
      } catch {
        if (fetchSequence !== this.paymentFundingQuoteFetchSequence || paymentId !== this.paymentId) return

        this.paymentFundingQuote = null
        this.paymentFundingQuoteState = 'error'
      }
    },

    async rejectPayment() {
      if (this.isCurrentProjectSender) return

      this.loadingDecline = true

      try {
        const payload = {
          request_id: this.paymentId
        }

        await this.rejectShiftPayments(payload)
        this.$emit('change', {}, true)
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.closeModal()
        this.loadingDecline = false
      }
    },

    async approveShifts() {
      if (this.isCurrentProjectSender) return

      this.loadingApprove = true
      try {
        const payload = {
          request_id: this.paymentId,
          shift_payment_ids: this.selectedShifts,
          reference_number: this.referenceNumber
        }

        this.paymentInfo = await this.approveShiftPayments(payload)
        this.refreshPaymentFundingQuote()

        this.$emit('change', {}, true)
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingApprove = false
        this.selectedShifts = []
      }
    },

    async declineShifts() {
      if (this.isCurrentProjectSender) return

      this.loadingDecline = true

      try {
        const payload = {
          request_id: this.paymentId,
          comment: this.cancelReason,
          shift_payment_ids: this.selectedShifts
        }

        this.paymentInfo = await this.declineShiftPayments(payload)
        this.closeDeclineModal()

        this.$emit('change', {}, true)
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingDecline = false
        this.selectedShifts = []
      }
    },

    updateShiftPaymentComment({ shiftPaymentIds, comment }) {
      this.paymentInfo = {
        ...this.paymentInfo,
        shift_payments: this.paymentInfo.shift_payments.map(item =>
          shiftPaymentIds.includes(item.shift_payment.id)
            ? { ...item, shift_payment: { ...item.shift_payment, comment } }
            : item
        )
      }
    },

    async fetchPaymentInfo() {
      if (!this.paymentId) {
        return
      }

      const paymentId = this.paymentId
      const fetchSequence = ++this.paymentFetchSequence

      this.loadingPayment = true
      this.intercompanyFetchSequence += 1
      this.resetPaymentFundingQuote()
      this.loadingIntercompanyInvoice = false
      this.intercompanyInvoice = null
      this.intercompanyInvoiceStatusUnavailable = false
      this.visible = true
      try {
        const paymentInfo = await this.getPayment({
          payment_id: paymentId
        })

        if (fetchSequence !== this.paymentFetchSequence || paymentId !== this.paymentId) {
          return
        }

        const requestPaymentStep = getRequestPaymentResumeStep(paymentInfo.payment?.status)
        if (requestPaymentStep !== undefined) {
          this.openRequestPayment(paymentInfo.payment)
          return
        }

        this.paymentInfo = paymentInfo
        this.refreshPaymentFundingQuote()

        if (paymentInfo.payment?.type === 'company_invoice') {
          this.refreshIntercompanyInvoice(paymentId)
        }
      } catch (error) {
        if (fetchSequence !== this.paymentFetchSequence || paymentId !== this.paymentId) {
          return
        }

        this.closeModal()
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        if (fetchSequence === this.paymentFetchSequence) {
          this.loadingPayment = false
        }
      }
    },

    async refreshIntercompanyInvoice(paymentId = this.paymentId) {
      if (!paymentId) {
        return
      }

      const fetchSequence = ++this.intercompanyFetchSequence
      this.loadingIntercompanyInvoice = true
      this.intercompanyInvoiceStatusUnavailable = false

      try {
        const invoice = await this.getStripeIntercompanyInvoice({
          payment_request_id: paymentId
        })

        if (fetchSequence !== this.intercompanyFetchSequence || paymentId !== this.paymentId) {
          return
        }

        this.intercompanyInvoice = invoice
      } catch (error) {
        if (fetchSequence !== this.intercompanyFetchSequence || paymentId !== this.paymentId) {
          return
        }

        this.intercompanyInvoice = null
        this.intercompanyInvoiceStatusUnavailable = true
      } finally {
        if (fetchSequence === this.intercompanyFetchSequence) {
          this.loadingIntercompanyInvoice = false
        }
      }
    },

    openBankAccountSettings() {
      this.closeModal()
      this.$router.push({ name: 'configuration-page.wallets' }).catch(() => {})
    },

    async refreshPaymentInfo() {
      if (!this.paymentId) {
        return
      }

      try {
        this.paymentInfo = await this.getPayment({
          payment_id: this.paymentId
        })
        this.refreshPaymentFundingQuote()
        if (this.paymentInfo.payment?.type === 'company_invoice') {
          this.refreshIntercompanyInvoice()
        }
      } catch (error) {
        this.closeModal()
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      }
    },

    deleteSelectedPayment() {
      if (this.isCurrentProjectSender) return

      this.openModal({
        type: 'deletion',
        data: {
          visible: true,
          title: 'Are you sure?',
          description: 'You are about to delete a payment.',
          onConfirm: async () => {
            const payload = {
              payment_id: this.paymentId
            }

            await this.deletePayment(payload)

            this.showNotification('success', 'Success', 'Payment has been deleted successfully')

            this.$emit('change', {}, true)
            this.closeModal()
          }
        }
      })
    }
  },
  mounted() {
    if (this.paymentId) {
      this.fetchPaymentInfo()
    }
  }
}
</script>

<style scoped>
::v-deep .ant-modal-body {
  overflow-y: auto;
  max-height: 600px;
}

.shifts-to-pay-table {
  height: 300px !important;
  overflow-y: auto;
}

.status-tag {
  margin: unset;
}

::v-deep .modal-fullscreen-wrap--expanded .ant-modal-body {
  max-height: none;
}

.modal-fullscreen-wrap--expanded .additional-info {
  position: static;
  padding: 0;
  width: 100%;
  margin: 0;
  padding-bottom: 24px;
}
</style>
